'use client'

import { CheckoutElementsProvider, PaymentElement, useCheckoutElements } from '@stripe/react-stripe-js/checkout'
import {
    type Appearance,
    loadStripe,
    type Stripe,
    type StripeCheckoutPaymentElementOptions,
    type StripePaymentElementChangeEvent
} from '@stripe/stripe-js'
import { Button } from '@tebuto/buttons/Button'
import { ComponentSpinner } from '@tebuto/layout/Spinner'
import { cn } from '@tebuto/lib/utils'
import { WidgetButton } from '@tebuto/widget/WidgetButton'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { isUnsafeStripeEmbedOrigin, stripeEmbedOriginErrorMessage } from './resolve-stripe-return-url'

const BUTTON_HEIGHT = 45
const PAYMENT_FORM_MIN_HEIGHT_PX = 140
const DEFAULT_LOADING_LABEL = 'Zahlungsformular wird geladen…'

export interface StripeCheckoutSubmitState {
    disabled: boolean
    isLoading: boolean
    submit: () => void
}

interface StripeCheckoutProps {
    clientSecret: string
    stripeConnectedAccountId: string
    /** @deprecated Set on the Checkout Session server-side; not passed to confirm(). */
    returnUrl?: string
    onSuccess: (checkoutSessionId: string) => Promise<void>
    isSubmitting?: boolean
    publishableKey: string
    showToastOnError?: boolean
    loadingLabel?: string
    appearance?: Appearance
    paymentElementOptions?: StripeCheckoutPaymentElementOptions
    /** Remount PaymentElement when the user picks a different online method row. */
    paymentElementRemountKey?: string
    useWidgetButton?: boolean
    showSubmitButton?: boolean
    onSubmitStateChange?: (state: StripeCheckoutSubmitState | null) => void
    onPaymentElementChange?: (event: StripePaymentElementChangeEvent) => void
    onReloadSession?: () => void
    reloadSessionLabel?: string
}

const stripePromiseCache: Record<string, Promise<Stripe | null>> = {}

function getStripePromise(publishableKey: string, stripeConnectedAccountId: string): Promise<Stripe | null> {
    const cacheKey = `${publishableKey}:${stripeConnectedAccountId}`
    if (!stripePromiseCache[cacheKey]) {
        stripePromiseCache[cacheKey] = loadStripe(publishableKey, {
            locale: 'de',
            stripeAccount: stripeConnectedAccountId
        })
    }
    return stripePromiseCache[cacheKey]
}

export function prefetchStripeJs(publishableKey: string, stripeConnectedAccountId: string): void {
    void getStripePromise(publishableKey, stripeConnectedAccountId)
}

function StripeLoadingOverlay({ label, widgetThemed = false }: { label: string; widgetThemed?: boolean }) {
    return (
        <div
            className={cn(
                'absolute inset-0 z-10 flex flex-col items-center justify-center gap-2',
                widgetThemed ? 'widget-bg-primary' : 'bg-white'
            )}
            aria-busy="true"
        >
            <ComponentSpinner className={cn('size-6', widgetThemed && 'widget-primary-600')} />
            <p className={cn('text-sm', widgetThemed ? 'widget-text-secondary' : 'text-foreground-secondary')}>
                {label}
            </p>
        </div>
    )
}

function StripePaymentForm({
    onSuccess,
    isSubmitting,
    showToastOnError = true,
    loadingLabel = DEFAULT_LOADING_LABEL,
    paymentElementOptions,
    paymentElementRemountKey,
    useWidgetButton = false,
    showSubmitButton = true,
    onSubmitStateChange,
    onPaymentElementChange,
    onReloadSession,
    reloadSessionLabel = 'Zahlung neu laden'
}: Omit<
    StripeCheckoutProps,
    'clientSecret' | 'stripeConnectedAccountId' | 'publishableKey' | 'returnUrl' | 'appearance'
>) {
    const checkoutState = useCheckoutElements()
    const [isProcessing, setIsProcessing] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [paymentElementReady, setPaymentElementReady] = useState(false)

    const handlePaymentElementReady = useCallback(() => {
        setPaymentElementReady(true)
    }, [])

    const handlePaymentElementChange = useCallback(
        (event: StripePaymentElementChangeEvent) => {
            onPaymentElementChange?.(event)
        },
        [onPaymentElementChange]
    )

    useEffect(() => {
        setPaymentElementReady(false)
    }, [paymentElementRemountKey])

    const handleSubmit = useCallback(async (): Promise<void> => {
        if (checkoutState.type !== 'success') {
            return
        }

        setIsProcessing(true)
        setSubmitError(null)
        try {
            if (isUnsafeStripeEmbedOrigin()) {
                const message = stripeEmbedOriginErrorMessage()
                setSubmitError(message)
                if (showToastOnError) {
                    toast.error(message)
                }
                return
            }

            const result = await checkoutState.checkout.confirm({
                redirect: 'if_required'
            })

            if (result.type === 'error') {
                const message = result.error.message ?? 'Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.'
                setSubmitError(message)
                if (showToastOnError) {
                    toast.error(message)
                }
                return
            }

            if (result.type !== 'success') {
                const message = 'Zahlung konnte nicht bestätigt werden.'
                setSubmitError(message)
                if (showToastOnError) {
                    toast.error(message)
                }
                return
            }

            const checkoutSessionId = result.session.id
            if (!checkoutSessionId) {
                const message = 'Zahlung konnte nicht bestätigt werden.'
                setSubmitError(message)
                if (showToastOnError) {
                    toast.error(message)
                }
                return
            }

            await onSuccess(checkoutSessionId)
        } catch (error) {
            console.error('Stripe checkout confirm failed', error)
            const message = 'Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.'
            setSubmitError(message)
            if (showToastOnError) {
                toast.error(message)
            }
        } finally {
            setIsProcessing(false)
        }
    }, [checkoutState, onSuccess, showToastOnError])
    const handleSubmitRef = useRef(handleSubmit)

    useEffect(() => {
        handleSubmitRef.current = handleSubmit
    }, [handleSubmit])

    const submitFromParent = useCallback(() => {
        void handleSubmitRef.current()
    }, [])

    const isCheckoutSessionError = checkoutState.type === 'error'
    const isPaymentElementReady =
        checkoutState.type === 'success' && paymentElementReady && !isUnsafeStripeEmbedOrigin()
    const canConfirm = checkoutState.type === 'success' ? checkoutState.checkout.canConfirm : false
    const isSubmitDisabled =
        checkoutState.type !== 'success' ||
        !paymentElementReady ||
        !canConfirm ||
        isProcessing ||
        isSubmitting ||
        isUnsafeStripeEmbedOrigin()
    const isExternallyLoading =
        !isCheckoutSessionError &&
        (checkoutState.type === 'loading' || !paymentElementReady || isProcessing || Boolean(isSubmitting))

    useEffect(() => {
        if (!onSubmitStateChange) {
            return
        }

        onSubmitStateChange({
            disabled: isSubmitDisabled,
            isLoading: isExternallyLoading,
            submit: submitFromParent
        })
    }, [isExternallyLoading, isSubmitDisabled, onSubmitStateChange, submitFromParent])

    useEffect(() => {
        if (!onSubmitStateChange) {
            return
        }
        return () => onSubmitStateChange(null)
    }, [onSubmitStateChange])

    if (checkoutState.type === 'loading') {
        return (
            <div
                className="relative flex w-full items-center justify-center"
                style={{ minHeight: PAYMENT_FORM_MIN_HEIGHT_PX }}
            >
                <StripeLoadingOverlay label={loadingLabel} widgetThemed={useWidgetButton} />
            </div>
        )
    }

    if (checkoutState.type === 'error') {
        return (
            <div className="flex flex-col gap-3">
                <p className="text-sm text-red-600">{checkoutState.error.message}</p>
                {onReloadSession ? (
                    <button
                        type="button"
                        data-testid="booking-payment-reload-checkout-session"
                        onClick={onReloadSession}
                        className={
                            useWidgetButton
                                ? 'min-h-11 w-full rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 transition-colors hover:bg-red-100'
                                : 'w-full rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 transition-colors hover:bg-red-100'
                        }
                    >
                        {reloadSessionLabel}
                    </button>
                ) : null}
            </div>
        )
    }

    if (isUnsafeStripeEmbedOrigin()) {
        return <p className="text-sm text-red-600">{stripeEmbedOriginErrorMessage()}</p>
    }

    const showLoadingOverlay = !isPaymentElementReady

    return (
        <div
            className="relative flex w-full flex-col gap-4"
            style={showLoadingOverlay ? { minHeight: PAYMENT_FORM_MIN_HEIGHT_PX } : undefined}
        >
            {showLoadingOverlay ? <StripeLoadingOverlay label={loadingLabel} widgetThemed={useWidgetButton} /> : null}
            <div
                className={cn('w-full', showLoadingOverlay && 'pointer-events-none absolute inset-x-0 top-0 opacity-0')}
            >
                <PaymentElement
                    key={paymentElementRemountKey ?? 'default'}
                    onReady={handlePaymentElementReady}
                    onChange={handlePaymentElementChange}
                    options={paymentElementOptions}
                />
            </div>
            {!showLoadingOverlay ? (
                <>
                    {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}
                    {showSubmitButton ? (
                        useWidgetButton ? (
                            <WidgetButton
                                type="button"
                                onClick={() => void handleSubmit()}
                                disabled={isSubmitDisabled}
                                isLoading={isProcessing || isSubmitting}
                                className="min-h-11 text-base"
                                data-testid="booking-payment-stripe-submit"
                            >
                                Buchung abschließen
                            </WidgetButton>
                        ) : (
                            <Button
                                onClick={() => void handleSubmit()}
                                disabled={isSubmitDisabled}
                                className="w-full"
                                style={{ height: BUTTON_HEIGHT }}
                            >
                                {isProcessing || isSubmitting ? (
                                    <ComponentSpinner className="size-5" />
                                ) : (
                                    'Buchung abschließen'
                                )}
                            </Button>
                        )
                    ) : null}
                </>
            ) : null}
        </div>
    )
}

export default function StripeCheckout({
    clientSecret,
    stripeConnectedAccountId,
    onSuccess,
    isSubmitting,
    publishableKey,
    showToastOnError,
    loadingLabel,
    appearance,
    paymentElementOptions,
    paymentElementRemountKey,
    useWidgetButton = false,
    showSubmitButton = true,
    onSubmitStateChange,
    onPaymentElementChange,
    onReloadSession,
    reloadSessionLabel
}: StripeCheckoutProps) {
    const stripePromise = useMemo(
        () => getStripePromise(publishableKey, stripeConnectedAccountId),
        [publishableKey, stripeConnectedAccountId]
    )

    const elementsAppearance = appearance ?? { theme: 'stripe' as const }

    if (!publishableKey) {
        return <p className="text-sm text-foreground-secondary">Stripe ist nicht konfiguriert.</p>
    }

    return (
        <CheckoutElementsProvider
            key={clientSecret}
            stripe={stripePromise}
            options={{
                clientSecret,
                elementsOptions: {
                    appearance: elementsAppearance
                }
            }}
        >
            <StripePaymentForm
                onSuccess={onSuccess}
                isSubmitting={isSubmitting}
                showToastOnError={showToastOnError}
                loadingLabel={loadingLabel}
                paymentElementOptions={paymentElementOptions}
                paymentElementRemountKey={paymentElementRemountKey}
                useWidgetButton={useWidgetButton}
                showSubmitButton={showSubmitButton}
                onSubmitStateChange={onSubmitStateChange}
                onPaymentElementChange={onPaymentElementChange}
                onReloadSession={onReloadSession}
                reloadSessionLabel={reloadSessionLabel}
            />
        </CheckoutElementsProvider>
    )
}
